import type { UploaderState } from '@iconik-io/uploader/src/state';

import ContextMenu from '../../components/context_menu/context_menu';
import contextMenuConnector from '../../components/context_menu/context_menu_connector';
import type {
  ContextMenuItemDefinition,
  GetDefinition,
} from '../../components/context_menu/types';
import {
  ASSET_PAGE_ROUTE,
  ASSET_VERSION_PAGE_ROUTE,
  COLLECTION_PAGE_ROUTE,
} from '../../constants/routes';
import {
  CONTEXT_MENU_UPLOADER_ITEMS,
  connectContextMenuConsumer,
} from '../../contexts/context_menu';
import { segmentTrackEvent } from '../../ducks/analytics_tracking/thunks';
import {
  PERMISSIONS_OPTIONS,
  TRANSCODE_PRIORITY_OPTIONS,
  UPLOADER_PERMISSIONS_COLLECTION,
  UPLOADER_PERMISSIONS_COLLECTION_LABEL,
  UPLOADER_PERMISSIONS_DEFAULT,
  UPLOADER_PERMISSIONS_DEFAULT_LABEL,
  UPLOADER_PERMISSIONS_PRIVATE,
  UPLOADER_PERMISSIONS_PRIVATE_LABEL,
} from '../../ducks/uploader/constants';
import {
  uploaderSetCollectionPermissions,
  uploaderSetTopLevelPermissions,
  uploaderSetTranscodePriority,
} from '../../ducks/uploader/slice';
import { ICONS } from '../../icons/icon';
import type { AppDispatch } from '../../store/types';
import { selectUserRoles } from '../../utils/get_user_roles';

export const getDefinition: GetDefinition<UploaderState> = (_props, state) => {
  const userRoles = selectUserRoles(state);
  const priority = state.uploaderReducer.transcodePriority;
  const items: ContextMenuItemDefinition[] = [];
  const canSetPermissions =
    ![ASSET_PAGE_ROUTE, ASSET_VERSION_PAGE_ROUTE].includes(
      state.routeParams.route,
    ) && userRoles.canWriteAcls;

  if (state.share.share.id) {
    return { items };
  }

  if (canSetPermissions) {
    const permissionsValue =
      state.routeParams.route === COLLECTION_PAGE_ROUTE
        ? state.uploaderReducer.collectionPermissions
        : state.uploaderReducer.topLevelPermissions;

    const permissionsSubitems = [];
    if (state.routeParams.route === COLLECTION_PAGE_ROUTE) {
      permissionsSubitems.push({
        label: UPLOADER_PERMISSIONS_COLLECTION_LABEL,
        action: () => (dispatch: AppDispatch) =>
          dispatch(
            uploaderSetCollectionPermissions(UPLOADER_PERMISSIONS_COLLECTION),
          ),
        checkable: true,
        checked:
          state.uploaderReducer.collectionPermissions ===
          UPLOADER_PERMISSIONS_COLLECTION,
      });
    }
    const permissionsAction =
      state.routeParams.route === COLLECTION_PAGE_ROUTE
        ? uploaderSetCollectionPermissions
        : uploaderSetTopLevelPermissions;
    permissionsSubitems.push(
      {
        label: UPLOADER_PERMISSIONS_PRIVATE_LABEL,
        action: () => (dispatch: AppDispatch) =>
          dispatch(permissionsAction(UPLOADER_PERMISSIONS_PRIVATE)),
        checkable: true,
        checked: permissionsValue === UPLOADER_PERMISSIONS_PRIVATE,
      },
      {
        label: UPLOADER_PERMISSIONS_DEFAULT_LABEL,
        action: () => (dispatch: AppDispatch) =>
          dispatch(permissionsAction(UPLOADER_PERMISSIONS_DEFAULT)),
        checkable: true,
        checked: permissionsValue === UPLOADER_PERMISSIONS_DEFAULT,
      },
    );

    items.push({
      label: `Permissions (${
        PERMISSIONS_OPTIONS.find((option) => option.value === permissionsValue)
          ?.label
      })`,
      icon: ICONS.Permission,
      subitems: permissionsSubitems,
    });
  }
  items.push({
    label: `Transcode Priority (${
      TRANSCODE_PRIORITY_OPTIONS.find((option) => option.value === priority)
        ?.label
    })`,
    icon: ICONS.Transcoder,
    subitems: TRANSCODE_PRIORITY_OPTIONS.map((option) => ({
      label: option.label,
      checkable: true,
      checked: option.value === priority,
      action: () => (dispatch: AppDispatch) => {
        dispatch(uploaderSetTranscodePriority(option.value));
        dispatch(
          segmentTrackEvent({
            eventName: 'Set Uploads Transcode Priority',
            properties: {
              Value: option.value,
            },
          }),
        );
      },
    })),
  });

  return {
    items,
  };
};

const UploaderContextMenu = connectContextMenuConsumer(
  contextMenuConnector(getDefinition)(ContextMenu),
  CONTEXT_MENU_UPLOADER_ITEMS,
);

export default UploaderContextMenu;
